import { useCallback } from 'react';
import { DOCUMENT } from '@config/constants';
import { useFindDocumentByGroup } from '@views/ProofDocuments/hooks/useFindDocumentByGroup';
import { useGetDocumentsInfoQuery } from '@views/ProofDocuments/graphql';
import { useAreDocumentsReady } from '@views/ProofDocuments/utils';
import { useDocumentForm } from '@views/ProofDocuments/hooks';
import { useGetAddressQuery } from '../graphql';
import { ts } from '../utils';

const formatAddress = ({ address }) => {
  const {
    zip,
    street,
    extNumber,
    neighborhood,
    municipality,
    state,
  } = address;

  // State name comes in uppercase.
  const stateName = state?.name.toLowerCase() || '';
  const intNumber = address?.intNumber || '';
  const addressLine1 = `${street} ${extNumber} ${intNumber}`;
  const addressLine2 = `${neighborhood}, ${municipality}, ${stateName}`;
  const addressLine3 = `${ts('zipCode')} ${zip}`;

  return {
    addressLine1,
    addressLine2,
    addressLine3,
  };
};

export const useController = () => {
  const {
    data: addressData,
    loading: isAddressLoading,
  } = useGetAddressQuery();
  const address = addressData?.user?.address || {};
  const {
    addressLine1,
    addressLine2,
    addressLine3,
  } = formatAddress({ address });

  const {
    control,
    resetForm,
    clearField,
    onUploadError,
    onUploadCompleted: onUploadCompletedForm,
  } = useDocumentForm();

  const {
    data: documentsData,
    loading: isDocumentsLoading,
    refetch: refetchDocuments,
  } = useGetDocumentsInfoQuery({
    notifyOnNetworkStatusChange: true,
  });

  const { document: addressDocument } = useFindDocumentByGroup({
    data: documentsData,
    klass: DOCUMENT.CLASS.PROOF_OF_ADDRESS,
    type: DOCUMENT.TYPE.PROOF_OF_ADDRESS,
    group: DOCUMENT.GROUPS.ADDRESS,
  });

  const neededDocuments = [addressDocument];

  const areDocumentsReady = useAreDocumentsReady({ neededDocuments });

  const onRemoveCompleted = useCallback(() => {
    resetForm();
    refetchDocuments();
  }, [
    resetForm,
    refetchDocuments,
  ]);

  const onUploadCompleted = useCallback(({ fieldName }) => {
    onUploadCompletedForm({ fieldName });
    refetchDocuments();
  }, [
    refetchDocuments,
    onUploadCompletedForm,
  ]);

  return {
    control,
    address,
    clearField,
    addressLine1,
    addressLine2,
    addressLine3,
    onUploadError,
    addressDocument,
    areDocumentsReady,
    isAddressLoading,
    onRemoveCompleted,
    onUploadCompleted,
    isDocumentsLoading,
  };
};
